import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { exportChartToPNG, downloadCSV, exportToExcel, generateFilename } from '../utils/exportUtils';
import './ExportButton.css';

/**
 * ExportButton Component
 * Provides export functionality for reports (CSV, Excel, PNG)
 */
const ExportButton = ({
    reportName,
    chartElementId,
    data,
    headers,
    keys,
    onExport,
    disabled = false
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [exporting, setExporting] = useState(false);

    const handleExport = async (format) => {
        setExporting(true);
        setShowMenu(false);

        try {
            if (onExport) {
                await onExport(format);
            } else {
                // Default export behavior
                switch (format) {
                    case 'csv':
                        if (data && headers && keys) {
                            const { convertToCSV } = await import('../utils/exportUtils');
                            const csvContent = convertToCSV(data, headers, keys);
                            downloadCSV(csvContent, generateFilename(reportName, 'csv'));
                        }
                        break;

                    case 'excel':
                        if (data) {
                            await exportToExcel(data, generateFilename(reportName, 'xlsx'), reportName);
                        }
                        break;

                    case 'png':
                        if (chartElementId) {
                            await exportChartToPNG(chartElementId, generateFilename(reportName, 'png'));
                        }
                        break;

                    default:
                        console.warn('Unknown export format:', format);
                }
            }
        } catch (error) {
            console.error('Export failed:', error);
            alert('Erreur lors de l\'export. Veuillez réessayer.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="export-button-container">
            <button
                className="btn btn-outline-primary export-button"
                onClick={() => setShowMenu(!showMenu)}
                disabled={disabled || exporting}
            >
                {exporting ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Export en cours...
                    </>
                ) : (
                    <>
                        <i className="bi bi-download me-2"></i>
                        Exporter
                        <i className={`bi bi-chevron-${showMenu ? 'up' : 'down'} ms-2`}></i>
                    </>
                )}
            </button>

            {showMenu && !exporting && (
                <>
                    <div className="export-menu-overlay" onClick={() => setShowMenu(false)}></div>
                    <div className="export-menu">
                        <div className="export-menu-header">Format d'export</div>

                        {data && (
                            <button
                                className="export-menu-item"
                                onClick={() => handleExport('excel')}
                            >
                                <i className="bi bi-file-earmark-excel"></i>
                                <div>
                                    <div className="export-menu-item-title">Excel</div>
                                    <div className="export-menu-item-desc">Fichier .xlsx</div>
                                </div>
                            </button>
                        )}

                        {chartElementId && (
                            <button
                                className="export-menu-item"
                                onClick={() => handleExport('png')}
                            >
                                <i className="bi bi-image"></i>
                                <div>
                                    <div className="export-menu-item-title">PNG</div>
                                    <div className="export-menu-item-desc">Image du graphique</div>
                                </div>
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

ExportButton.propTypes = {
    reportName: PropTypes.string.isRequired,
    chartElementId: PropTypes.string,
    data: PropTypes.array,
    headers: PropTypes.array,
    keys: PropTypes.array,
    onExport: PropTypes.func,
    disabled: PropTypes.bool
};

export default ExportButton;
